import React from 'react';
import { motion } from 'motion/react';
import { assetUrl, handleImgError } from '../utils/imageHelpers';

// Hero "Ola Dorada" (V1) — port fiel del handoff de Claude Design.
// Paleta de marca: rojo #820000 / #5e0000 + dorado #e0a03d / #f4bd52.

const NAVBAR_OFFSET = 80; // altura del Navbar fijo

const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - NAVBAR_OFFSET;
  window.scrollTo({ top, behavior: 'smooth' });
};

const ArrowRight = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

const HeroSection = () => {
  // Ambos CTAs llevan al menú: no existe (todavía) una sección de promos
  // independiente; combos y promos viven dentro del flujo del menú.
  const goToMenu = () => scrollToSection('menu');

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{
        fontFamily: "'Archivo', sans-serif",
        background:
          'radial-gradient(120% 130% at 18% 22%, #9a0d0d 0%, #820000 42%, #5e0000 100%)',
      }}
      aria-label="Bienvenido a la Juanita"
    >
      <div className="relative mx-auto w-full max-w-[1440px] min-h-[100svh]">
        {/* Ola dorada — solo desktop. Entra por arriba y sale por el borde DERECHO
            antes de llegar al fondo: el bajo del hero queda rojo y funde con la
            página (también roja), evitando el filo plano dorado→rojo. */}
        <svg
          className="hidden md:block absolute inset-0 h-full w-full"
          viewBox="0 0 1440 780"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path
            d="M1440,0 L1015,0 C880,150 905,335 760,440 C665,515 705,630 575,705 C780,778 1095,735 1440,650 Z"
            fill="#e0a03d"
          />
          <path
            d="M1440,0 L1110,0 C990,165 1010,360 865,468 C775,532 815,648 700,726 C885,792 1160,756 1440,688 Z"
            fill="#eeb24a"
            opacity="0.55"
          />
        </svg>

        {/* Marca de agua: sello real de La Juanita (cara + "LA JUANITA 20·23"),
            tenue, detrás del plato — solo desktop */}
        <img
          src={assetUrl('/images/logo.png')}
          alt=""
          aria-hidden="true"
          className="hidden md:block pointer-events-none select-none absolute right-[4%] bottom-[4%] w-[clamp(440px,44vw,600px)] opacity-[0.18] z-[2]"
        />

        {/* Composición principal */}
        <div className="relative z-[5] grid grid-cols-1 md:grid-cols-2 items-center min-h-[inherit] gap-6 md:gap-4 px-6 sm:px-10 lg:pl-24 lg:pr-8 pt-28 pb-14 md:py-12">
          {/* Columna de texto */}
          <motion.div
            className="text-center md:text-left max-w-[600px] mx-auto md:mx-0"
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            {/* Kicker */}
            <span className="inline-flex items-center gap-2.5 text-[#f4bd52] font-bold text-[13px] sm:text-[15px] tracking-[0.22em] uppercase">
              <span className="inline-block w-[34px] h-[2px] bg-[#f4bd52]" aria-hidden="true" />
              Bienvenido a casa
            </span>

            {/* Titular */}
            <h1 className="mt-5 text-white leading-[0.96]" style={{ letterSpacing: '-0.01em' }}>
              <span className="block font-['Archivo_Black'] text-[clamp(2.5rem,7vw,58px)]">
                Bienvenido a
              </span>
              <span
                className="block font-['Kaushan_Script'] text-[#f4bd52] text-[clamp(3.5rem,11vw,96px)] leading-[0.92] -mt-0.5"
                style={{ textShadow: '0 6px 22px rgba(0,0,0,0.28)' }}
              >
                la Juanita
              </span>
            </h1>

            {/* Subcopy */}
            <p className="mt-7 mx-auto md:mx-0 max-w-[520px] text-[#fbe6cf] font-semibold leading-[1.4] text-[clamp(1.25rem,3vw,30px)]">
              Ordena comidas <b className="text-[#f4bd52] font-extrabold">DELICIOSAS</b> a precios{' '}
              <b className="text-[#f4bd52] font-extrabold">AMIGABLES</b>, hechas como en casa.
            </p>

            {/* Etiquetas de horario */}
            <div className="mt-7 flex gap-3 flex-wrap justify-center md:justify-start">
              {['Lunes – viernes', '11:00 AM – 3:00 PM'].map((label) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 rounded-[30px] border border-white/20 bg-white/[0.12] px-[18px] py-[9px] text-[15px] font-semibold text-white whitespace-nowrap"
                  style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
                >
                  <span className="w-[7px] h-[7px] rounded-full bg-[#f4bd52]" aria-hidden="true" />
                  {label}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="mt-9 flex items-center gap-5 flex-wrap justify-center md:justify-start">
              <button
                type="button"
                onClick={goToMenu}
                className="group inline-flex items-center gap-2.5 rounded-[14px] bg-white px-[34px] py-[17px] text-[18px] font-extrabold text-[#820000] shadow-[0_14px_34px_rgba(0,0,0,0.30)] transition-[transform,box-shadow] duration-200 hover:-translate-y-[3px] hover:shadow-[0_20px_40px_rgba(0,0,0,0.38)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4bd52]"
              >
                Ver el menú
                <span className="transition-transform duration-200 group-hover:translate-x-1">
                  <ArrowRight />
                </span>
              </button>
              <button
                type="button"
                onClick={goToMenu}
                className="text-[16px] font-semibold text-white opacity-85 transition-opacity duration-200 hover:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#f4bd52]"
              >
                Ver promo de inicio →
              </button>
            </div>
          </motion.div>

          {/* Columna del platillo */}
          <motion.div
            className="relative flex justify-center md:justify-end items-center"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.15 }}
          >
            {/* Resplandor dorado detrás del plato (suaviza el plato sobre el rojo en mobile) */}
            <div
              className="md:hidden absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] aspect-square rounded-full"
              style={{
                background:
                  'radial-gradient(circle, rgba(244,189,82,0.30) 0%, rgba(224,160,61,0) 62%)',
              }}
              aria-hidden="true"
            />

            {/* Marca de agua (sello real) detrás del plato — mobile */}
            <img
              src={assetUrl('/images/logo.png')}
              alt=""
              aria-hidden="true"
              className="md:hidden pointer-events-none select-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[135%] max-w-none opacity-[0.13]"
            />

            <div className="relative z-[1] w-full max-w-[300px] sm:max-w-[420px] md:max-w-[560px] lg:max-w-[680px] drop-shadow-[0_36px_50px_rgba(0,0,0,0.45)] lg:-mr-[10px]">
              <picture>
                <source srcSet={assetUrl('/images/platillo.avif')} type="image/avif" />
                <source srcSet={assetUrl('/images/platillo.webp')} type="image/webp" />
                <img
                  src={assetUrl('/images/platillo.png')}
                  alt="Platillo casero de La Juanita: rollo de carne relleno con puré de papa y espagueti verde"
                  className="lj-floaty block w-full"
                  width="1400"
                  height="1034"
                  fetchpriority="high"
                  onError={handleImgError}
                />
              </picture>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
